import React, { useEffect, useRef, useState } from 'react';
import { Camera, ChevronLeft, ChevronRight, X } from 'lucide-react';

export const GamePhotoStrip = ({ game, compact = false }) => {
  const photos = game?.result?.photoUrls || [];
  const [index, setIndex] = useState(null);
  const touchStart = useRef(null);
  const count = photos.length;

  useEffect(() => {
    if (index === null) return undefined;
    const previous = document.body.style.overflow;
    const onKey = (event) => {
      if (event.key === 'Escape') setIndex(null);
      if (event.key === 'ArrowLeft') setIndex((i) => (i - 1 + count) % count);
      if (event.key === 'ArrowRight') setIndex((i) => (i + 1) % count);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = previous; window.removeEventListener('keydown', onKey); };
  }, [index, count]);

  if (!count) return null;
  const label = `${game.opponent || 'Game'} · ${game.date}`;
  const step = (delta) => setIndex((i) => (i + delta + count) % count);
  const onTouchEnd = (event) => {
    if (touchStart.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStart.current;
    touchStart.current = null;
    if (Math.abs(delta) > 40) step(delta < 0 ? 1 : -1);
  };

  return (
    <>
      <div className={`flex gap-1.5 ${compact ? 'mt-2' : 'mt-3'}`} data-testid={`game-photos-${game.id}`}>
        {photos.map((url, i) => (
          <button key={url} type="button" onClick={() => setIndex(i)} aria-label={`Open game photo ${i + 1} of ${count}`} data-testid={`game-photo-${game.id}-${i + 1}`} className={`group relative overflow-hidden border border-white/30 bg-[#071226] ${compact ? 'h-12 w-12' : 'h-16 w-16 sm:h-20 sm:w-20'} shrink-0 hover:border-[#f4b400] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f4b400]`}>
            <img src={url} alt={`${label} sideline photo ${i + 1}`} className="h-full w-full object-cover transition-transform group-hover:scale-105" loading="lazy" decoding="async" />
            {i === 0 && <span className="absolute left-1 top-1 flex h-5 w-5 items-center justify-center bg-[#c8102e] text-white"><Camera className="h-3 w-3" aria-hidden="true" /></span>}
          </button>
        ))}
      </div>
      {index !== null && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#061027]/95 p-3 backdrop-blur-sm sm:p-8" role="dialog" aria-modal="true" aria-label={`${label} photos`} data-testid={`game-photos-modal-${game.id}`} onClick={() => setIndex(null)}>
          <div className="relative flex h-[92dvh] w-full max-w-5xl flex-col border-t-4 border-[#c8102e] bg-black" onClick={(event) => event.stopPropagation()} onTouchStart={(event) => { touchStart.current = event.touches[0].clientX; }} onTouchEnd={onTouchEnd}>
            <div className="flex shrink-0 items-center justify-between gap-3 bg-[#0a1f44] px-4 py-2 text-white"><div className="font-heading text-xs uppercase tracking-widest" data-testid={`game-photos-position-${game.id}`}>{index + 1} of {count} · {label}</div><button type="button" onClick={() => setIndex(null)} aria-label="Close photos" data-testid={`game-photos-close-${game.id}`} className="flex h-11 w-11 items-center justify-center hover:text-[#c8102e]"><X className="h-6 w-6" /></button></div>
            <img src={photos[index]} alt={`${label} sideline photo ${index + 1} full size`} className="min-h-0 flex-1 object-contain" data-testid={`game-photos-full-${game.id}`} />
            {count > 1 && <><button type="button" onClick={() => step(-1)} aria-label="Previous photo" data-testid={`game-photos-prev-${game.id}`} className="absolute left-2 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center bg-[#0a1f44]/90 text-white hover:bg-[#c8102e] sm:left-4"><ChevronLeft className="h-7 w-7" /></button><button type="button" onClick={() => step(1)} aria-label="Next photo" data-testid={`game-photos-next-${game.id}`} className="absolute right-2 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center bg-[#0a1f44]/90 text-white hover:bg-[#c8102e] sm:right-4"><ChevronRight className="h-7 w-7" /></button></>}
          </div>
        </div>
      )}
    </>
  );
};
